import { ICellRendererParams, IHeaderParams } from "ag-grid-community";
import { ArrowDown, ArrowUp, Info } from "lucide-react";
import React, { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  LabelList,
  ReferenceLine,
  ReferenceLineProps,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from "recharts";

import { IgAnalyticsTooltip } from "@intelligems/ig-theme/IgAnalyticsTooltip";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Stack } from "@mui/system";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { UpliftPlusMinus } from "@src/features/analytics/baseDashboard/UpliftPlusMinus";
import { getTextWidth } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { WideWhiteTooltip } from "@src/components/custom-tooltips/WideWhiteTooltip";
import {
  findMaxCiRangeValue,
  findMinCiRangeValue,
  labelFormatWrapper,
} from "@src/features/analytics/metricDetails/statSig/utils";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import {
  BarMetricData,
  HistogramMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";
import { StatSigData } from "@src/types/analytics-tinybird";
import { statSigPage } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

const StatSigCustomizedAxisTick = (
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  props: any & {
    groupMetricData:
      | BarMetricData[]
      | HistogramMetricData[]
      | StackedBarMetricData[];
    config: GraphConfig;
    storeCurrency: { code: string; locale: string };
  }
) => {
  const { x, y, payload, width } = props.props;
  const shouldShowDecimals =
    Math.min(...props.groupMetricData.map((d) => d.value)) < 0.09;

  return (
    <Text
      fill={"rgba(0, 0, 0, 0.38)"}
      maxLines={2}
      fontSize={10}
      fontFamily={theme.typography.fontFamily}
      x={x}
      y={y - 10}
      width={getTextWidth(false, props.groupMetricData!.length, width)}
      textAnchor="middle"
      verticalAnchor={"start"}
    >
      {labelFormatWrapper(
        payload.value,
        props.config.dataTypes[0],
        props.storeCurrency,
        shouldShowDecimals
      )}
    </Text>
  );
};

export const BaseStatSigHeaderTooltip = ({
  tooltipKey,
  textOnly,
}: {
  tooltipKey: string;
  textOnly?: boolean;
}) => {
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);

  const selectedBaseline = variations.find(
    (v) => v.id === dashboardFilters.compareGroupA
  );
  const theme = useTheme();
  const textMap = {
    uplift: {
      title: "Uplift",
      description: `This column shows the percentage change in the metric compared to the ${selectedBaseline?.name} group, indicating the impact of changes or trends.
Positive values signify an improvement, while negative values reflect a decline.`,
    },
    p2bb: {
      title: "Probability to Be Best",
      description: `This column indicates the likelihood that a variant outperforms all others in the table for the given metric, based on statistical analysis.
A higher probability suggests stronger confidence in its performance.`,
    },
    p2bc: {
      title: `Probability to Beat ${selectedBaseline?.name}`,
      description: `This column represents the likelihood that a variant performs better than the ${selectedBaseline?.name} group for the given metric.
A higher value indicates greater confidence that the variant is an improvement over the ${selectedBaseline?.name} group.`,
    },
    interval: {
      title: "Value and Confidence Intervals",
      description: `This column shows the estimated value of this metric and how certain we are about that estimate at the moment.
The narrower the bars and the less overlap between groups, the more certain you can be that there is a meaningful difference between your test groups.`,
    },
  };
  const content = () => {
    return (
      <Stack sx={{ width: "400px", paddingLeft: 2 }} spacing={2}>
        <Grid columnSpacing={2} container direction={"row"} wrap={"nowrap"}>
          <Grid>
            <Info size={20} />
          </Grid>
          <Grid>
            <IgTypography sx={{ fontWeight: 600 }}>
              {textMap[tooltipKey].title}
            </IgTypography>
          </Grid>
        </Grid>
        <Grid>
          <IgTypography color={theme.palette.text.secondary}>
            {textMap[tooltipKey].description}
          </IgTypography>
        </Grid>
        <Grid container justifyContent={"flex-end"}>
          <IgButton
            onClick={() => {
              openNewTab(statSigPage, "Statistical Significance");
            }}
          >
            Learn More
          </IgButton>
        </Grid>
      </Stack>
    );
  };
  if (textOnly) {
    return content();
  }
  return (
    <WideWhiteTooltip title={content()}>
      <IgTypography
        color={theme.palette.text.secondary}
        sx={{ fontWeight: 600 }}
      >
        {textMap[tooltipKey].title}
      </IgTypography>
    </WideWhiteTooltip>
  );
};

export const StatSigIntervalHeader = memo(
  (
    props: IHeaderParams & {
      statSigData: StatSigData[];
      config: GraphConfig;
      isLoading: boolean;
      hasValueCI: boolean;
    }
  ) => {
    const { statSigData, config, isLoading, hasValueCI } = props;
    const theme = useTheme();
    const graphValueFormatter = useGraphValueFormatter();
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    if (isLoading || !statSigData) {
      return (
        <WideWhiteTooltip
          title={
            <BaseStatSigHeaderTooltip textOnly={true} tooltipKey={"interval"} />
          }
        >
          <Grid
            container
            direction={"column"}
            justifyContent={"center"}
            textAlign={"center"}
            alignItems={"center"}
          >
            <Grid>
              <IgTypography
                fontWeight={600}
                color={theme.palette.text.secondary}
                variant={"body2"}
              >
                Value
              </IgTypography>
            </Grid>
            <Grid>
              <Skeleton
                variant="rectangular"
                width={500}
                height={15}
                sx={{ borderRadius: "5px" }}
              />
            </Grid>
          </Grid>
        </WideWhiteTooltip>
      );
    }

    const computedRanges = statSigData.filter(
      (data) => data.ci_range !== undefined
    );
    if (!hasValueCI || computedRanges.length === 0) {
      return (
        <WideWhiteTooltip
          title={
            <BaseStatSigHeaderTooltip textOnly={true} tooltipKey={"interval"} />
          }
        >
          <Grid
            container
            direction={"column"}
            justifyContent={"center"}
            textAlign={"center"}
            alignItems={"center"}
          >
            <Grid>
              <IgTypography
                fontWeight={600}
                color={theme.palette.text.secondary}
              >
                Value
              </IgTypography>
            </Grid>
          </Grid>
        </WideWhiteTooltip>
      );
    }
    const dataMax = findMaxCiRangeValue(
      statSigData,
      storeCurrencyConfig!.value
    );
    const dataMin = findMinCiRangeValue(
      statSigData,
      storeCurrencyConfig!.value
    );

    const tickCount = 9;
    const step = (dataMax - dataMin) / (tickCount - 1);

    // custom ticks to enforce 9 ticks

    const customTicks = useMemo(() => {
      return Array.from(
        { length: tickCount },
        (_, i) => +(dataMin + i * step).toFixed(4)
      );
    }, [statSigData, dataMin, dataMax]);

    return (
      <WideWhiteTooltip
        title={
          <BaseStatSigHeaderTooltip textOnly={true} tooltipKey={"interval"} />
        }
      >
        <Grid
          container
          textAlign={"center"}
          justifyContent={"center"}
          direction={"column"}
        >
          <Grid sx={{ padding: "0px !important", marginBottom: "-10px" }}>
            <IgTypography color={theme.palette.text.secondary} fontWeight={600}>
              Value
            </IgTypography>
          </Grid>
          <Grid>
            <ResponsiveContainer width="100%" height={29}>
              <BarChart layout="vertical" data={statSigData}>
                <YAxis
                  tick={false}
                  dataKey="variation_id"
                  type="category"
                  tickLine={false}
                  hide={true}
                />
                <XAxis
                  tickCount={9}
                  ticks={customTicks}
                  padding={{ left: 15, right: 15 }}
                  axisLine={{ stroke: theme.igBorders.core.disabled }}
                  tickLine={false}
                  interval={"preserveStartEnd"}
                  domain={[dataMin, dataMax]}
                  orientation={"top"}
                  type="number"
                  dataKey={"ci_range"}
                  tick={(props: any) => {
                    return (
                      // @ts-ignore
                      <StatSigCustomizedAxisTick
                        props={props}
                        groupMetricData={statSigData}
                        graphValueFormatter={graphValueFormatter}
                        config={config}
                        storeCurrency={storeCurrencyConfig!.value}
                      />
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </WideWhiteTooltip>
    );
  }
);

const HorizonBar = (props: ReferenceLineProps) => {
  const { x, y, width, height } = props;

  return (
    <line
      x1={props.x1}
      y1={((props?.y1 as number) || 0) - 15}
      x2={props.x2}
      y2={((props?.y2 as number) || 0) + 15}
      stroke={props.stroke}
      strokeWidth={2}
    />
  );
};

export const StatSigCellInterval = (
  props: ICellRendererParams & {
    statSigData: StatSigData[];
    config: GraphConfig;
    isLoading: boolean;
    hasValueCI: boolean;
  }
) => {
  const theme = useTheme();
  const { statSigData, config, data, isLoading, hasValueCI } = props;
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  if (!statSigData || !data || isLoading) {
    return (
      <Grid container direction={"row"} justifyContent={"center"}>
        <Skeleton
          variant="rectangular"
          width={500}
          height={15}
          sx={{ borderRadius: "5px" }}
        />
      </Grid>
    );
  }

  if (!hasValueCI || !data.ci_range) {
    return (
      <Grid container direction={"row"} justifyContent={"center"}>
        <IgTypography
          variant={"body2"}
          sx={{ color: theme.palette.text.secondary }}
        >
          Not Enough Data
        </IgTypography>
      </Grid>
    );
  }
  const dataMax = findMaxCiRangeValue(statSigData, storeCurrencyConfig!.value);
  const dataMin = findMinCiRangeValue(statSigData, storeCurrencyConfig!.value);
  const shouldShowDecimals =
    Math.min(...statSigData.map((d) => d.value)) < 0.09;
  return (
    <Grid>
      <ResponsiveContainer width="100%" height={60}>
        <BarChart layout="vertical" maxBarSize={20} data={[data]}>
          <XAxis
            hide={true}
            tickLine={false}
            domain={[dataMin, dataMax]}
            type="number"
            dataKey={"ci_range"}
            tick={false}
            interval={0}
            tickCount={9}
            padding={{ left: 18, right: 15 }}
          />
          <YAxis
            hide={true}
            tick={false}
            dataKey="variation_id"
            type="category"
            tickLine={false}
            axisLine={{ stroke: "transparent" }}
          />
          <Bar dataKey="ci_range" fill={theme.igPrimitives.gray[500]}>
            <LabelList
              fontSize={12}
              dataKey={"ci_range[0]"}
              position={"left"}
              formatter={(value: any) =>
                labelFormatWrapper(
                  value,
                  config.dataTypes[0],
                  storeCurrencyConfig!.value,
                  shouldShowDecimals
                )
              }
            />
            <LabelList
              fontSize={12}
              dataKey={"ci_range[1]"}
              position={"right"}
              formatter={(value: any) =>
                labelFormatWrapper(
                  value,
                  config.dataTypes[0],
                  storeCurrencyConfig!.value,
                  shouldShowDecimals
                )
              }
            />
          </Bar>
          <ReferenceLine
            shape={<HorizonBar />}
            x={data.value}
            stroke={theme.igPrimitives.gray["A400"]}
            strokeWidth={2}
          />
        </BarChart>
      </ResponsiveContainer>
    </Grid>
  );
};

export const UpliftCellRenderer = (
  props: ICellRendererParams & {
    storeCurrencyConfig: { code: string; locale: string };
    baselineVariationId: string;
    highestVariationId: string;
    isLoading: boolean;
    selectedConfig: GraphConfig;
  }
) => {
  const theme = useTheme();
  const {
    storeCurrencyConfig,
    data,
    baselineVariationId,
    value,
    isLoading,
    selectedConfig,
  } = props;

  if (isLoading || !data) {
    return (
      <Skeleton
        variant="rectangular"
        width={31}
        height={15}
        sx={{ borderRadius: "5px" }}
      />
    );
  }
  if (data.variation_id === baselineVariationId) {
    return (
      <IgTypography sx={{ color: theme.palette.text.secondary }}>
        n/a
      </IgTypography>
    );
  }

  if (!data?.uplift?.value) {
    return (
      <IgAnalyticsTooltip title={"Not enough data"}>
        <IgTypography sx={{ color: theme.palette.text.secondary }}>
          &mdash;
        </IgTypography>
      </IgAnalyticsTooltip>
    );
  }

  const uplift = `${new Intl.NumberFormat(
    storeCurrencyConfig.locale || "en-US",
    { style: "decimal", maximumFractionDigits: 2, minimumFractionDigits: 1 }
  ).format(data.uplift.value * 100)}%`;

  return (
    <Grid
      container
      direction={"row"}
      alignItems={"center"}
      columnSpacing={0.5}
      wrap={"nowrap"}
      justifyContent={"center"}
    >
      {parseInt(uplift) === 0 ? null : parseInt(uplift) > 0 ? (
        <ArrowUp
          size={14}
          style={{ marginRight: "5px" }}
          color={
            selectedConfig.impactCalculationColoring === "inverted"
              ? theme.igIconColors.error
              : selectedConfig.impactCalculationColoring === "neutral"
                ? theme.palette.text.primary
                : theme.igIconColors.success
          }
        />
      ) : (
        <ArrowDown
          size={14}
          style={{ marginRight: "5px" }}
          color={
            selectedConfig.impactCalculationColoring === "inverted"
              ? theme.igIconColors.success
              : selectedConfig.impactCalculationColoring === "neutral"
                ? theme.palette.text.primary
                : theme.igIconColors.error
          }
        />
      )}

      <IgTypography sx={{ marginRight: "5px" }}>
        {`${new Intl.NumberFormat(storeCurrencyConfig.locale || "en-US", {
          style: "decimal",
          maximumFractionDigits: 1,
        }).format(data.uplift.value * 100)}%`}
      </IgTypography>
      <UpliftPlusMinus
        typographyProps={{ variant: "body2" }}
        customFormatterOptions={{
          maximumSignificantDigits: 2,
          minimumSignificantDigits: 1,
          minimumFractionDigits: 1,
        }}
        upliftWrapper={{
          upliftCiHigh:
            data?.uplift?.ci_range && data?.uplift?.ci_range.length
              ? data?.uplift?.ci_range[1]
              : undefined,
          upliftCiLow:
            data?.uplift?.ci_range && data?.uplift?.ci_range.length
              ? data?.uplift?.ci_range[0]
              : undefined,
        }}
      />
    </Grid>
  );
};

export const GroupCellRenderer = (
  props: ICellRendererParams & { variations: VariationFe[] }
) => {
  return (
    <IgTypography
      sx={{
        color: theme.palette.text.secondary,
        fontStyle: props.value === "All Groups" ? "italic" : "none",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {" "}
      <IgAnalyticsTooltip
        title={props.variations.find((v) => v.id === props.value)?.name || ""}
      >
        <>{props.variations.find((v) => v.id === props.value)?.name || ""}</>
      </IgAnalyticsTooltip>
    </IgTypography>
  );
};

export const ValueCellRenderer = (
  props: ICellRendererParams & { isP2bc: boolean; baselineVariationId: string }
) => {
  const theme = useTheme();
  if (
    props.isP2bc &&
    props?.data["variation_id"] === props.baselineVariationId
  ) {
    return (
      <IgTypography sx={{ color: theme.palette.text.secondary }}>
        n/a
      </IgTypography>
    );
  } else if (props.value === undefined || props.value === null) {
    return (
      <IgAnalyticsTooltip title={"Not enough data"}>
        <IgTypography sx={{ color: theme.palette.text.secondary }}>
          &mdash;
        </IgTypography>
      </IgAnalyticsTooltip>
    );
  } else {
    return (
      <IgTypography sx={{ color: 400 }}>{props.valueFormatted}</IgTypography>
    );
  }
};
